import { useHistory, useParams } from 'react-router-dom';
import { useState, useEffect, useContext, Fragment } from 'react';
import { useDispatch } from 'react-redux';
import {useTranslation} from 'react-i18next';

import useFetch from '../../../../hooks/use-fetch';
import UserContext from '../../../../store/user-context';
import ErrorScreen from '../../../UI/ErrorScreen';

import SceneContainer from './SceneContainer';
import Loader from '../../../UI/Loader';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Parrot from '../../../../assets/images/parrot-cutout-bitesize.png';
import useAnalyticsEventTracker from '../../../../hooks/use-analytics';
import ParrotHelper from '../../../Town/Tutorial/ParrotHelper';

const ConversationLanding = () => {
  const {t: translate } = useTranslation();
  const dispatch = useDispatch();
  const [locationObjectives, setLocationObjectives] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openObjective, setOpenObjective] = useState('');
  const { user, setUser } = useContext(UserContext);
  const { apiUrl } = window['appConfig'];
  const gaEventTracker = useAnalyticsEventTracker('Scene Selection');
  const history = useHistory();

  const params = useParams();
  let location = params.location;
  location = location.toLowerCase();

  const isTutorial = location === 'tutorial' ? true : false;

  const locationUrl = `${apiUrl}/objective/location/${location}/`;
  const progressUrl = `${apiUrl}/progress/location/${location}/`;

  const {
    data: locationData,
    error: locationError,
    initFunc: initLocationFetch,
  } = useFetch();
  const {
    data: progressData,
    error: progressError,
    initFunc: initProgressFetch,
  } = useFetch();

  const fetchOptions = {
    method: 'GET',
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  };

  const maybeOpenScenes = (objective_index, objective) => {
    if (window.innerWidth < 1024) {
      setOpenObjective(objective_index);
      gaEventTracker('Select Objective', `${location} ${objective.objective}`)
    }
  };

  useEffect(() => {
    if (user.showLandingLink === true) {
      setUser((prevState) => ({
        ...prevState,
        showLandingLink: false,
      }));
    }

    //TODO: remove in future when conversation parts are in redux
    dispatch({
      type: 'setNpcAudioBlobs',
      payload: { blobs: [] },
    });
    //TODO: remove in future when conversation parts are in redux
    dispatch({
      type: 'setAudioBlobs',
      payload: { blobs: [] },
    });

    dispatch({
      type: 'resetCurrentLesson',
    });

    initLocationFetch({ url: locationUrl, options: fetchOptions });
    initProgressFetch({ url: progressUrl, options: fetchOptions });
  }, []);

  useEffect(() => {
    if (locationData !== null && progressData !== null) {
      for (let i = 0; i < locationData.length; i++) {
        let scenes = locationData[i].scenes;
        //get scene id and find the lessons from progressData
        for (let j = 0; j < scenes.length; j++) {
          let sceneId = scenes[j];

          //Catch to ensure the whole object has not been passed for some reason
          if (typeof sceneId !== 'string') {
            sceneId = sceneId.id;
          }

          //Update old data to now be object with id and lessons
          locationData[i].scenes[j] = {
            id: sceneId,
            lessons: { ...progressData[sceneId].lessons },
          };
        }
      }

      setLocationObjectives(locationData);
      setIsLoading(false);
    }
  }, [locationData, progressData]);

  const startTutorial = (sceneId, lessonNumber) => {
    const link = `/conversation/${location}/${sceneId}/${lessonNumber}`;

    if (sceneId) {
      dispatch({
        type: 'startLesson',
        payload: { sceneID: sceneId, lesson: lessonNumber },
      });
    }

    history.push(link);
  }

  let content = (
    <p>{translate('no-conversations')}</p>
  );

  if (locationObjectives.length > 0 && ! isTutorial) {
    content = locationObjectives.map((objective, index) => {
      let showModal = openObjective === index;

      if (window.innerWidth >= 768) {
        showModal = true;
      }

      return (
        <div className='objective-container' key={index}>
          <div className='objective' key={index} onClick={() => { maybeOpenScenes(index, objective); }}>
            <img src={Parrot} className='objective__icon' alt={objective.objective} title={objective.objective} />
            <div className='objective__info'>
              <h4 className='objective__name'>{objective.objective}</h4>
              <p className='objective__level'>{translate('level')} {objective.level}</p>
            </div>
            <div className="objective__link" >
              <FontAwesomeIcon icon={faChevronRight} size="1x" />
            </div>
          </div>
          {showModal ? (
            <SceneContainer
              objectiveName={objective.objective}
              scenes={objective.scenes}
              level={objective.level}
              closeModal={setOpenObjective}
            />
          ) : (
            ''
          )}
        </div>
      );
    });
  } else if(locationObjectives.length > 0 && isTutorial) {
    content = 
      <div className='objective-container' onClick={() => {startTutorial(locationObjectives[0].scenes[0].id, 1)}}>
        <div className='objective objective--tutorial'>
          <img src={Parrot} className='objective__icon' alt={locationObjectives[0].objective} title={locationObjectives[0].objective} />
          <div className='objective__info'>
            <h4 className='objective__name'>{locationObjectives[0].objective}</h4>
            <p className='objective__level'>{translate('level')} {locationObjectives[0].level}</p>
          </div>
          <div className="objective__link">
            <FontAwesomeIcon icon={faChevronRight} size="1x" />
          </div>
        </div>
      </div>
  }

  if (locationError || progressError) {
    content = (
      <ErrorScreen>{locationError + '\n\n' + progressError}</ErrorScreen>
    );
  }

  return (
    <Fragment>
      <div className="masthead">
        <h1 className='masthead__title'>{translate('conversation-landing-welcome', {location: location})}</h1>
      </div>
      <section className="section section__conversation-landing">
        <div className="container">
          <div className="objectives">
            {! isLoading && ! isTutorial && <p className='objective-helptext'>{translate('landing-help-text')}</p>}
            {! isLoading &&  isTutorial && <ParrotHelper parrotNumber={2} isActive={!isLoading} styleOverrides={{top: window.innerWidth < 768 ? '0px' : '60px'}}>{translate('click-objective')}</ParrotHelper>}
            {isLoading ? <Loader>{translate('loading')}...</Loader> : content}
          </div>
        </div>
      </section>
    </Fragment>
  );
};

export default ConversationLanding;
